import React from "react"

import type { AgentInfo } from "@/agents/info"
import {
  AudioFilterDetails,
  AudioFilterToggle,
} from "@/metrics/registry/configuration"
import { supportsAudioFilter, type MetricRecord } from "@/metrics/runtime"

import type { SimulatorType } from "@/types/api/SimulatorType"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type { AudioFilterConfig } from "@/types/metrics/metadata"

import { audioOnlyMetricEnabled } from "../is-metric-enabled-helpers"

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TypeScript mixin constructors require any[] rest params
type Constructor<T = object> = new (...args: any[]) => T

/**
 * Mixin that gates a metric to voice-only simulators and appends the shared
 * audio-filter editor to the record's editor metadata.
 *
 * Subclasses that override `renderEditorMetadata` must call
 * `super.renderEditorMetadata(...)` to keep the audio-filter appended.
 */
export function WithAudioGateMixin<T extends Constructor<MetricRecord>>(
  Base: T,
) {
  return class extends Base {
    isMetricEnabled(
      agent: AgentInfo,
      simulatorType: SimulatorType,
    ): { result: boolean; error_message: string } {
      return audioOnlyMetricEnabled(simulatorType)
    }

    renderEditorMetadata(
      metricRecordData: MetricRecordData,
      onChange: (metric: MetricRecordData) => void,
      textAreaRef?: React.RefObject<HTMLTextAreaElement>,
      editorContext?: Parameters<MetricRecord["renderEditorMetadata"]>[3],
    ): React.ReactNode {
      const baseNode = super.renderEditorMetadata(
        metricRecordData,
        onChange,
        textAreaRef,
        editorContext,
      )

      if (!supportsAudioFilter(metricRecordData)) {
        return baseNode
      }

      const audioFilter = metricRecordData.metric_metadata?.audio_filter
      if (editorContext?.readOnly && audioFilter === undefined) {
        return baseNode
      }
      const handleAudioFilterChange = (next: AudioFilterConfig | undefined) => {
        const { audio_filter: _prev, ...rest } =
          metricRecordData.metric_metadata ?? {}
        onChange({
          ...metricRecordData,
          metric_metadata:
            next === undefined ? rest : { ...rest, audio_filter: next },
        })
      }

      return (
        <div className="flex flex-col gap-4">
          {baseNode}
          <AudioFilterToggle
            value={audioFilter}
            onChange={handleAudioFilterChange}
          />
          <AudioFilterDetails
            value={audioFilter}
            onChange={handleAudioFilterChange}
            metricManager={metricRecordData.metric_manager}
          />
        </div>
      )
    }
  }
}
